/* eslint-disable prettier/prettier */
/* eslint-disable @nx/enforce-module-boundaries */
import { Button, Grid, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { parse } from 'qs';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ICaseStatisticsData } from '@bvi/api-interfaces/response/case';
import {
  ResultsCaseCounts,
  ResultsChart,
  ResultsAveragesByYearChart,
  ResultsProminentCases,
  ResultsTable,
  StatisticsAlert,
  SearchTags,
  ISearchFormDataSchema,
  buildParametersForSettings,
  SearchReport,
} from '@bvi/cases-search';
import { PaperModal } from '@bvi/common-components';
import { selectSettingsData } from '@bvi/dashboard/entities/search/selectors';
import { SaveSearchForm } from '@bvi/dashboard/feature/saved-searches/ui/save-search-form';
import { SearchSavedMessage } from '@bvi/dashboard/feature/saved-searches/ui/search-saved-message';
import { DateFormat, formatDate } from '@bvi/date-utils';

type SearchResultProperties = {
  statisticsData: ICaseStatisticsData;
  isFetching: boolean;
};

export const SearchResult: React.FC<SearchResultProperties> = ({
  statisticsData,
  isFetching,
}) => {
  const { t } = useTranslation('', {
    keyPrefix: 'search.results',
  });

  const [isSearchSavedMessageOpen, setSearchSavedMessageOpen] = useState(false);
  const handleOpenSearchSavedMessage = () => setSearchSavedMessageOpen(true);
  const handleCloseSearchSavedMessage = () => setSearchSavedMessageOpen(false);

  const [isSaveSearchModalOpen, setSaveSearchModalOpen] = useState(false);
  const handleOpenSaveSearchModal = () => setSaveSearchModalOpen(true);
  const handleCloseSaveSearchModal = () => setSaveSearchModalOpen(false);
  const handleCloseSaveSearchModalOnSuccess = () => {
    handleCloseSaveSearchModal();
    handleOpenSearchSavedMessage();
  };

  const location = useLocation();
  const searchParameters = parse(location.search, {
    comma: true,
    ignoreQueryPrefix: true,
  }) as Record<keyof ISearchFormDataSchema, string>;

  const settingsData = useSelector(
    selectSettingsData(buildParametersForSettings(searchParameters)),
  );

  const searchReportReference = useRef<HTMLDivElement>(null);
  const handleDownloadPdf = async () => {
    const document_ = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
    });

    const currentElement = searchReportReference.current;
    if (currentElement) {
      const canvas = await html2canvas(currentElement);
      const { offsetHeight } = currentElement;
      const imgData = canvas.toDataURL('image/jpeg');
      const fileName = `search-report_${formatDate(
        new Date(),
        DateFormat.defaultFullTime,
      )}`;

      const width = 448;
      const height = offsetHeight / 4;
      document_.addImage(imgData, 'jpeg', 0, 0, width, height);
      document_.save(fileName);
    }
  };

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={2}>
          <Typography variant="h1">{t('title')}</Typography>
        </Grid>
        <Grid
          container
          item
          xs={10}
          columnSpacing={2}
          justifyContent="flex-end"
        >
          <Grid item>
            <Button variant="outlined" size="small" onClick={handleDownloadPdf}>
              {t('pdfButton')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenSaveSearchModal}
            >
              {t('saveSearchButton')}
            </Button>
            <PaperModal
              open={isSaveSearchModalOpen}
              onClose={handleCloseSaveSearchModal}
            >
              <SaveSearchForm
                parameters={searchParameters}
                onSuccess={handleCloseSaveSearchModalOnSuccess}
                onCancel={handleCloseSaveSearchModal}
              />
            </PaperModal>
            <PaperModal
              open={isSearchSavedMessageOpen}
              onClose={handleCloseSearchSavedMessage}
            >
              <SearchSavedMessage
                onBackToSearch={handleCloseSearchSavedMessage}
              />
            </PaperModal>
          </Grid>
        </Grid>
      </Grid>
      <SearchTags data={settingsData} isLoading={isFetching} />
      <StatisticsAlert data={statisticsData} isLoading={isFetching} />
      <ResultsTable data={statisticsData} isLoading={isFetching} shouldSticky />
      <ResultsChart data={statisticsData} isLoading={isFetching} shouldSticky />
      <ResultsAveragesByYearChart
        averagesByYear={statisticsData.averagesByYear}
        isLoading={isFetching}
      />
      <ResultsProminentCases
        prominentCases={statisticsData.prominentCases}
        isLoading={isFetching}
      />
      <ResultsCaseCounts
        data={statisticsData}
        isLoading={isFetching}
        shouldSticky
      />
      <SearchReport
        ref={searchReportReference}
        statisticsData={statisticsData}
        settingsData={settingsData}
      />
    </>
  );
};
